/**
 * @file ThemeSwitcher 主题色卡切换器
 * @description 以迷你后台预览 + 色条展示全部主题，点击即切换并持久化。
 *
 * @whenToUse 设置页、组件预览页、需要用户自选主题的入口。
 * @whenNotToUse
 * 1. 仅程序内切换主题（直接 `useThemeStore.getState().setTheme`）。
 * 2. 业务页装饰性色块（用语义 Token，不要复制本组件预览逻辑）。
 */

import { Check } from 'lucide-react'
import { themes, type ThemeDefinition, type ThemeId } from '@/lib/themes.ts'
import { useThemeStore } from '@/stores/theme-store.ts'

const swatchKeys = [
  'canvas',
  'surface',
  'primary',
  'accent',
  'success',
  'warning',
  'danger',
] as const satisfies ReadonlyArray<keyof ThemeDefinition>

type ThemeSwitcherProps = {
  /** 追加到栅格容器的 class */
  className?: string
}

export function ThemeSwitcher({ className = '' }: ThemeSwitcherProps) {
  const themeId = useThemeStore((state) => state.theme)
  const setTheme = useThemeStore((state) => state.setTheme)

  return (
    <div className={`grid gap-5 sm:grid-cols-2 xl:grid-cols-3 ${className}`.trim()}>
      {themes.map((palette) => (
        <ThemeCard
          key={palette.id}
          palette={palette}
          active={palette.id === themeId}
          onSelect={setTheme}
        />
      ))}
    </div>
  )
}

type ThemeCardProps = {
  /** 色卡对应主题 */
  palette: ThemeDefinition
  /** 是否为当前选中主题 */
  active: boolean
  /** 选中回调 */
  onSelect: (id: ThemeId) => void
}

function ThemeCard({ palette, active, onSelect }: ThemeCardProps) {
  return (
    <button
      type="button"
      onClick={() => onSelect(palette.id)}
      aria-pressed={active}
      className="rounded-2xl border p-4 text-left transition-colors duration-200 focus-visible:opacity-90"
      style={{
        backgroundColor: palette.surface,
        borderColor: active ? palette.primary : palette.border,
        color: palette.text,
      }}
    >
      <div className="mb-3 flex items-start justify-between gap-3">
        <div>
          <div className="text-base font-semibold">{palette.name}</div>
          <div className="mt-1 text-xs" style={{ color: palette.muted }}>
            {palette.tagline}
          </div>
        </div>
        {active ? (
          <span
            className="inline-flex size-6 items-center justify-center rounded-full"
            style={{
              backgroundColor: palette.primary,
              color: palette.primaryForeground,
            }}
          >
            <Check className="size-3.5" aria-hidden />
          </span>
        ) : null}
      </div>

      <div
        className="mb-3 overflow-hidden rounded-xl"
        style={{
          backgroundColor: palette.canvas,
          border: `1px solid ${palette.border}`,
        }}
      >
        <div className="flex h-28">
          <aside
            className="flex w-14 flex-col gap-2 p-2"
            style={{
              backgroundColor: palette.surface,
              borderRight: `1px solid ${palette.border}`,
            }}
          >
            <div
              className="h-2 rounded-full"
              style={{ backgroundColor: palette.primary, width: '70%' }}
            />
            <div
              className="h-1.5 rounded-full"
              style={{ backgroundColor: palette.border, width: '90%' }}
            />
            <div
              className="h-1.5 rounded-full"
              style={{ backgroundColor: palette.border, width: '60%' }}
            />
            <div
              className="mt-auto h-6 rounded-md"
              style={{ backgroundColor: palette.accent, opacity: 0.45 }}
            />
          </aside>
          <main className="flex flex-1 flex-col gap-2 p-2">
            <div className="flex items-center justify-between">
              <div
                className="h-2 w-16 rounded-full"
                style={{ backgroundColor: palette.text, opacity: 0.75 }}
              />
              <div
                className="flex h-5 items-center rounded-md px-2 text-xs font-medium"
                style={{
                  backgroundColor: palette.primary,
                  color: palette.primaryForeground,
                  transform: 'scale(0.85)',
                  transformOrigin: 'right center',
                }}
              >
                新建
              </div>
            </div>
            <div
              className="flex-1 rounded-lg p-2"
              style={{
                backgroundColor: palette.raised,
                border: `1px solid ${palette.border}`,
              }}
            >
              <div
                className="mb-2 h-1.5 w-2/3 rounded-full"
                style={{ backgroundColor: palette.muted, opacity: 0.5 }}
              />
              <div
                className="h-1.5 w-1/2 rounded-full"
                style={{ backgroundColor: palette.border }}
              />
              <div className="mt-3 flex gap-2">
                <span
                  className="rounded-sm px-1.5 py-0.5 text-xs font-medium"
                  style={{
                    backgroundColor: `${palette.success}33`,
                    color: palette.success,
                    transform: 'scale(0.85)',
                    transformOrigin: 'left center',
                  }}
                >
                  成功
                </span>
                <span
                  className="rounded-sm px-1.5 py-0.5 text-xs font-medium"
                  style={{
                    backgroundColor: `${palette.warning}33`,
                    color: palette.warning,
                    transform: 'scale(0.85)',
                    transformOrigin: 'left center',
                  }}
                >
                  警告
                </span>
              </div>
            </div>
          </main>
        </div>
      </div>

      <div className="flex overflow-hidden rounded-md">
        {swatchKeys.map((key) => (
          <div
            key={key}
            className="h-6 flex-1"
            style={{ backgroundColor: palette[key] }}
            title={`${key}: ${palette[key]}`}
          />
        ))}
      </div>
    </button>
  )
}
